import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { doFetchMailbox } from 'actions/mailboxes'
import useIsMounted from 'util/hooks/useIsMounted'
import {
  selectCurrentChannelById,
  selectPendingChannelById,
} from 'ducks/channels/selectors'
import {
  doOpenSearchPageWithMailboxAndFolder,
  doAddGmailChannel,
  doOauthGmailChannel,
  doAddMessengerChannel,
  doReconnectMessengerChannel,
  doAddInstagramChannel,
  doReconnectInstagramChannel,
} from 'ducks/channels/actions'
import { DRAWER_TYPE_CHANNELS_MEMBERS_EDIT } from 'ducks/drawers/types'
import {
  doBuildInboxMenuFromMailboxes,
  doBuildMenuFromWidgets,
  doRedirectToCollectionAndFolderById,
} from 'ducks/folders/operations/collections'
import { doFetchFacebookPages } from 'ducks/facebook/actions'
import { doFetchWidgetById } from 'ducks/widgets/operations'
import { doGmailImport } from 'ducks/mailboxes/actions'
import { isBoostrappedSelector } from 'selectors/app/base'
import { selectAgentTotalCount } from 'selectors/agents/base'
import { useProductFeature } from 'ducks/billing/hooks'
import EDIT_MEMBERS_CONFIG from 'subapps/settings/components/drawers/Channels/EditMembers/config'
import CONFIG from 'subapps/settings/components/drawers/Channels/ChannelDetails/config'
import usePrevious from 'util/hooks/usePrevious'
import { isChatChannelType } from './channelTypes'

export const useChannel = (channelId, { useCachedIfAvailable = true } = {}) => {
  const isMounted = useIsMounted()
  const dispatch = useDispatch()
  const stateChannel = useSelector(state =>
    selectCurrentChannelById(state, channelId)
  )

  // NOTE: make sure to change the useEffect below if the useState default below changes
  const [channel, setChannel] = useState(
    useCachedIfAvailable ? stateChannel : null
  )
  const isBootstrapped = useSelector(isBoostrappedSelector)

  // NOTE: make sure to change the useEffect below if any of the useState defaults below change
  const [isLoading, setIsLoading] = useState(null)
  const [isLoaded, setIsLoaded] = useState(!!channel)
  const [isMissing, setIsMissing] = useState(false)
  const [loadCount, setLoadCount] = useState(0)
  const previousChannelId = usePrevious(channelId)

  const loadChannel = useCallback(
    async options => {
      const { ignoreCache = false } = options || {}
      let loadedChannel = channel
      if (
        (!loadedChannel || !useCachedIfAvailable || ignoreCache) &&
        channelId &&
        channelId !== 'new'
      ) {
        setIsLoading(true)
        setLoadCount(loadCount + 1)
        if (channelId.startsWith('wid')) {
          loadedChannel = await dispatch(
            doFetchWidgetById(channelId, { useCachedIfAvailable })
          )
        } else {
          loadedChannel = await dispatch(
            doFetchMailbox(channelId, { useCachedIfAvailable })
          )
        }
      }
      if (!isMounted()) return
      if (!loadedChannel) {
        setIsMissing(true)
      } else {
        setIsLoading(false)
        setIsLoaded(true)
      }
    },
    [
      dispatch,
      channel,
      setIsLoading,
      setIsMissing,
      channelId,
      useCachedIfAvailable,
      loadCount,
      isMounted,
    ]
  )

  // Mailboxes are loaded as part of the bootstrap, so technically speaking this load isnt required except
  // for a small edgecase where the owner/admin has setup a mailbox and then denied themselfs access to it.
  // Fetching a mailbox by id however will still returns the mailbox if your are an admin, but dont have permission
  // to it.
  useEffect(
    () => {
      if (isLoading !== null || !isBootstrapped || !isMounted()) return
      loadChannel()
    },
    [loadChannel, isLoading, isBootstrapped, isMounted]
  )

  useEffect(
    () => {
      if (isLoaded && channel !== stateChannel) {
        setChannel(stateChannel)
      }
    },
    [isLoaded, channel, stateChannel]
  )

  useEffect(
    () => {
      // re-initialize if the channelId changes or cached channel = null BUT stateChannel has value
      if (
        (previousChannelId && channelId !== previousChannelId) ||
        (!channel && stateChannel)
      ) {
        const resetChannel = useCachedIfAvailable ? stateChannel : null
        setChannel(resetChannel)
        setIsLoading(null)
        setIsLoaded(!!resetChannel)
        setIsMissing(false)
        setLoadCount(0)
      }
    },
    [channelId, previousChannelId, stateChannel, useCachedIfAvailable, channel]
  )

  return {
    channel,
    isLoading,
    isReloading: isLoading && loadCount > 1,
    isMissing,
    loadChannel,
    loadCount,
  }
}

export const useRebuildLeftNavMenu = ({ channelId, onExit }) => {
  const dispatch = useDispatch()
  const { channel } = useChannel(channelId)
  const { channel_type: channelType } = channel || {}

  const rebuildMenu = useCallback(
    () => {
      if (isChatChannelType(channelType)) {
        dispatch(doBuildMenuFromWidgets({ autoRedirect: false }))
      } else {
        dispatch(doBuildInboxMenuFromMailboxes())
      }
    },
    [dispatch, channelType]
  )

  const rebuildMenuAndExit = useCallback(
    () => {
      rebuildMenu()
      onExit()
    },
    [onExit, rebuildMenu]
  )

  return {
    rebuildMenu,
    rebuildMenuAndExit,
  }
}

export const useRedirectToChannel = ({ channelId, onExit }) => {
  const dispatch = useDispatch()
  const { rebuildMenu } = useRebuildLeftNavMenu({ channelId, onExit })
  const { channel } = useChannel(channelId)
  const { channel_type: channelType } = channel || {}
  const { folders } = channel || {}

  const handleRedirect = useCallback(
    () => {
      if (isChatChannelType(channelType)) {
        dispatch(doRedirectToCollectionAndFolderById(channelId))
      } else {
        dispatch(
          doOpenSearchPageWithMailboxAndFolder(
            channelId,
            folders ? folders[0].id : null,
            {
              query: {
                created: 'inbox',
              },
            }
          )
        )
      }
    },
    [dispatch, channelId, channelType, folders]
  )

  const handleExitAndRedirect = useCallback(
    () => {
      onExit()
      handleRedirect()
    },
    [onExit, handleRedirect]
  )

  const handleRebuildExitAndRedirect = useCallback(
    () => {
      rebuildMenu()
      handleExitAndRedirect()
    },
    [handleExitAndRedirect, rebuildMenu]
  )

  const handleRebuildAndRedirect = useCallback(
    () => {
      rebuildMenu()
      handleRedirect()
    },
    [handleRedirect, rebuildMenu]
  )

  return {
    redirect: handleRedirect,
    exitAndRedirect: handleExitAndRedirect,
    rebuildAndRedirect: handleRebuildAndRedirect,
    rebuildExitAndRedirect: handleRebuildExitAndRedirect,
  }
}

export const useEstablishChannelConnection = ({
  channelId,
  channelType,
  action,
  source,
}) => {
  const dispatch = useDispatch()

  const pendingChannel = useSelector(state =>
    selectPendingChannelById(state, channelId)
  )
  const currentChannel = useSelector(state =>
    selectCurrentChannelById(state, channelId)
  )

  const handleGmailChannel = useCallback(
    async () => {
      let mailbox = null
      if (source === 'inbox') {
        mailbox = await dispatch(doOauthGmailChannel(channelId))
        await dispatch(
          doGmailImport(channelId, {
            import_labels: false,
            close_tickets: false,
            pages_to_import: 2,
          })
        )
      } else {
        mailbox = await dispatch(doAddGmailChannel(pendingChannel))
      }
      return mailbox
    },
    [dispatch, pendingChannel, source, channelId]
  )

  const handleMessengerChannelInstall = useCallback(
    async () => {
      const widget = await dispatch(doAddMessengerChannel(pendingChannel))
      return widget
    },
    [dispatch, pendingChannel]
  )

  const handleInstagramChannelInstall = useCallback(
    async () => {
      const widget = await dispatch(doAddInstagramChannel(pendingChannel))
      return widget
    },
    [dispatch, pendingChannel]
  )

  const handleMessengerChannelReconnect = useCallback(
    async () => {
      await dispatch(doReconnectMessengerChannel(currentChannel))
      const { bridge: { externalId, externalName } = {} } = currentChannel
      if (externalId) {
        const { facebookPages } = await dispatch(
          doFetchFacebookPages({ skipLoaded: false })
        )
        const linkedFacebookPage = facebookPages.find(p => {
          return p.uid === externalId && p.subscribed
        })
        if (!linkedFacebookPage) {
          throw new Error(
            `Please also give permission for the page “${externalName}“.`
          )
        }
      }
      return currentChannel
    },
    [dispatch, currentChannel]
  )

  const handleInstagramChannelReconnect = useCallback(
    async () => {
      await dispatch(doReconnectInstagramChannel(currentChannel))
      return currentChannel
    },
    [dispatch, currentChannel]
  )

  const connectChannel = useCallback(
    async () => {
      if (channelType === 'google') {
        return handleGmailChannel()
      } else if (channelType === 'facebook') {
        if (action === 'install') {
          return handleMessengerChannelInstall()
        }
        return handleMessengerChannelReconnect()
      } else if (channelType === 'instagram') {
        if (action === 'install') {
          return handleInstagramChannelInstall()
        }
        return handleInstagramChannelReconnect()
      }
      return null
    },
    [
      channelType,
      handleGmailChannel,
      handleMessengerChannelInstall,
      handleMessengerChannelReconnect,
      handleInstagramChannelInstall,
      handleInstagramChannelReconnect,
      action,
    ]
  )

  return { connectChannel }
}

export const useChannelDetailsConfig = channelType => {
  const agentsTotalCount = useSelector(selectAgentTotalCount)
  const { drawer: nextDrawer, ...rest } = CONFIG[channelType]
  const { product, feature } = rest
  const { canUseFeature } = useProductFeature(product, feature)

  // next drawer after edit members drawer
  const { drawer: nextDrawerAfterEditMembersStep } = EDIT_MEMBERS_CONFIG[
    channelType
  ]

  const skipMembersEditDrawer =
    agentsTotalCount <= 1 &&
    nextDrawer?.type === DRAWER_TYPE_CHANNELS_MEMBERS_EDIT

  // There are no other teammates, skip the edit team members drawer and rather open the next drawer
  const drawer = skipMembersEditDrawer
    ? nextDrawerAfterEditMembersStep
    : nextDrawer

  return {
    ...rest,
    drawer,
    canUseFeature,
  }
}
